import { useEffect, useRef, useState } from "react";
import {
  get, isUnknown, readFileAsBase64, send,
  type EpicorImportPreview, type ImportApplyResult, type ImportBatch, type ImportPreview,
  type PastImport, type PastImportBatch, type PastImportPlan, type Unknowable, type VanishBrake,
} from "../api.js";
import { chooseImportTarget, scenarioNameKey, type ImportTarget } from "../../domain/import/target.js";
import { formatCT } from "../time.js";

/**
 * The morning file.
 *
 * Two steps, deliberately: preview writes nothing to the schedule (an Epicor
 * preview keeps only the rows it pulled, for the apply), and apply writes the
 * project rows and records the import against one draft scenario to re-plan
 * in — today's unless the person names another. Apply writes exactly what the
 * preview showed: it sends back the preview's plan hash (and, for Epicor, the
 * snapshot the preview kept), and the server refuses with "preview again"
 * rather than write anything that changed in between. Neither reassigns anyone, in
 * that draft or the published schedule; a new project's steps only join every
 * scenario's pool, unassigned. The reader has to be able to tell that from the
 * screen, not from documentation, because "I uploaded the file and it
 * rescheduled the shop" is the failure this shape exists to prevent.
 *
 * A past import reopens as the same plan, read-only. It takes the screen
 * while it is open but leaves any preview in progress exactly as it was —
 * hidden, not unmounted, so even the file input keeps the file it holds:
 * "Back to imports" closes it and that preview is there again, Apply and all,
 * with whatever error line it had.
 */
export function Import({ role, onScenario }: {
  role?: string;
  onScenario?: (scenarioId: string) => void;
}) {
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<ImportApplyResult | null>(null);
  // One field serves both flows: each retires the other's preview, so only one
  // is ever on screen to be applied.
  const [targetName, setTargetName] = useState("");
  const [batches, setBatches] = useState<ImportBatch[]>([]);
  const [epicorPreview, setEpicorPreview] = useState<EpicorImportPreview | null>(null);
  // Set on the first 503 and kept for the rest of the session — there is
  // nothing to poll for, since the server isn't going to grow an ERP port
  // mid-session, and re-asking on every render would just repeat the same 503.
  const [epicorUnavailable, setEpicorUnavailable] = useState(false);
  // A past import opened from the list. Nothing else is cleared to show it,
  // so closing it gives back whatever preview was on screen.
  const [past, setPast] = useState<PastImport | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  // The error line the screen had when a past import opened, given back on
  // Back, and the row button to give focus back to.
  const errBeforePast = useRef<string | null>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const loadBatches = async () => {
    try { setBatches((await get<{ batches: ImportBatch[] }>("/import/batches")).batches); }
    catch { /* the history is nice to have; its absence must not block an import */ }
  };
  useEffect(() => { void loadBatches(); }, []);

  async function openPast(batchId: string, from: HTMLElement | null) {
    if (busy || opening) return;
    setOpening(batchId);
    try {
      const got = await get<PastImport>(`/import/batches/${encodeURIComponent(batchId)}`);
      errBeforePast.current = err; setErr(null);
      returnFocus.current = from;
      setPast(got);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setOpening(null);
    }
  }

  function closePast() {
    setPast(null); setErr(errBeforePast.current);
    errBeforePast.current = null;
  }
  // Once the list is showing again, focus goes back to the row it was opened from.
  useEffect(() => {
    if (past) return;
    returnFocus.current?.focus();
    returnFocus.current = null;
  }, [past]);

  async function choose(f: File) {
    // Starting the file flow retires whatever the Epicor flow left on screen —
    // otherwise its plan and counts go stale the moment this one applies.
    setFile(f); setPreview(null); setDone(null); setErr(null); setEpicorPreview(null);
    setBusy("Reading the file…");
    try {
      const contentBase64 = await readFileAsBase64(f);
      setBusy("Working out what changed…");
      const p = await send<ImportPreview>("/import/preview", "POST", {
        contentBase64, filename: f.name,
      });
      setTargetName(p.defaultScenarioName ?? ""); setPreview(p);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function apply() {
    if (!file) return;
    setBusy("Applying…"); setErr(null); setDone(null);
    try {
      const contentBase64 = await readFileAsBase64(file);
      const r = await send<ImportApplyResult>("/import/apply", "POST", {
        contentBase64, filename: file.name, scenarioName: scenarioNameToSend(),
        planHash: preview?.planHash,
      });
      setDone(r); setPreview(null); setFile(null);
      if (input.current) input.current.value = "";
      await loadBatches();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  /**
   * A 503 here means the deployment has no ERP port at all — not a transient
   * fault, so the button is worth replacing rather than retrying. Anything
   * else (502 on an incomplete or malformed pull, 409 when something changed
   * since the preview, 404 or 410 when the preview's snapshot is gone or a day
   * old) is the same one-line error the file path already shows — each of those
   * says to preview again.
   */
  function epicorFailed(e: unknown) {
    if ((e as Error).message === "epicor_unavailable") setEpicorUnavailable(true);
    else setErr((e as Error).message);
  }

  async function pullEpicor() {
    // Starting the Epicor flow retires whatever the file flow left on screen,
    // same reasoning as choose() clearing epicorPreview.
    setEpicorPreview(null); setErr(null); setDone(null); setPreview(null); setFile(null);
    if (input.current) input.current.value = "";
    setBusy("Pulling from Epicor…");
    try {
      const p = await send<EpicorImportPreview>("/import/epicor/preview", "POST");
      setTargetName(p.defaultScenarioName ?? ""); setEpicorPreview(p);
    } catch (e) {
      epicorFailed(e);
    } finally {
      setBusy(null);
    }
  }

  async function applyEpicor() {
    setBusy("Applying…"); setErr(null); setDone(null);
    try {
      const r = await send<ImportApplyResult>("/import/epicor/apply", "POST", {
        scenarioName: scenarioNameToSend(),
        snapshotId: epicorPreview?.snapshotId, planHash: epicorPreview?.planHash,
      });
      setDone(r); setEpicorPreview(null);
      await loadBatches();
    } catch (e) {
      epicorFailed(e);
    } finally {
      setBusy(null);
    }
  }

  const plan = preview?.plan;
  // The verdict the apply will reach for whichever preview is showing, so the
  // hint and the disabled button can never disagree with the server.
  const shown = preview ?? epicorPreview;
  // A server older than this screen sends no scenario list. Judging an absent
  // list crashed the whole page, so without one there is no verdict: the server
  // still decides on apply, and the note below says why the hint is missing.
  const serverKnowsTargets = !!shown && Array.isArray(shown.scenarios) && !!shown.defaultScenarioName;
  const target = serverKnowsTargets
    ? chooseImportTarget(shown!.scenarios, targetName, shown!.defaultScenarioName)
    : null;
  // A refused target disables Apply; its label must not still promise a draft.
  const applyLabel = target?.kind === "refuse" ? "Apply"
    : target?.kind === "existing" ? "Apply — adds to that draft" : "Apply — creates a draft to review";
  // The prefilled day name goes as blank, so the server names the day by its
  // own clock: a preview before UTC midnight applied after it would otherwise
  // ask for yesterday's name, which strict rules may then refuse.
  const scenarioNameToSend = () =>
    serverKnowsTargets && scenarioNameKey(targetName) === scenarioNameKey(shown!.defaultScenarioName) ? "" : targetName;

  return (
    <div>
      <p className="hint" style={{ marginTop: 0 }}>
        The file brings projects and their due dates, and nothing else. Steps, hours, skills
        and assignments are yours and are never overwritten by it. Applying updates the
        projects straight away and records the import against a draft scenario to re-plan
        in — <b>no existing assignment changes</b>, in that draft or the published schedule.
        A new project's steps arrive unassigned, at the bottom of the pool, in every scenario.
      </p>
      {err && <div className="err" style={{ marginBottom: 12 }}>{err}</div>}

      {past && <PastImportView past={past} onBack={closePast} onScenario={onScenario} />}
      <div hidden={!!past}>
      {role === "admin" ? (
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 16 }}>
          <input ref={input} type="file" accept=".xlsx"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void choose(f); }} />
          {epicorUnavailable ? (
            <span className="muted">Epicor pull is not configured on this server</span>
          ) : (
            <button className="btn" disabled={!!busy} onClick={() => void pullEpicor()}>
              Pull from Epicor
            </button>
          )}
          {busy && <span className="muted">{busy}</span>}
        </div>
      ) : (
        <p className="muted">
          Importing the daily file is an admin's. The history below is the record of every one.
        </p>
      )}

      {done && (
        <div className="run-sec">
          <h3>Applied<span className="run-sec-note">{done.applied} change{done.applied === 1 ? "" : "s"}</span></h3>
          {done.scenarioId ? (
            <p>
              The changes went into {done.scenarioCreated ? "a new draft" : "the existing draft"}{" "}
              <b>{done.scenarioName}</b>. Project details and due dates are already updated, and
              nobody was reassigned — re-plan in that draft before publishing it. New steps sit
              unassigned at the bottom of the pool in every scenario, the live one included.{" "}
              {onScenario && (
                <button className="btn on" onClick={() => onScenario(done.scenarioId!)}>
                  Open the draft
                </button>
              )}
            </p>
          ) : (
            <p className="muted">Nothing had changed, so no scenario was created.</p>
          )}
        </div>
      )}

      {plan && preview && (
        <>
          <PlanView preview={preview} returnedLastSeen={preview.returnedLastSeen} archivedIds={preview.archivedIds} />
          {!preview.empty && target && (
            <TargetField preview={preview} value={targetName} onChange={setTargetName} target={target} />
          )}
          {!preview.empty && !serverKnowsTargets && (
            <p className="muted">This server is older than the screen, so the scenario can't be chosen here. Changes go into today's Day draft. Restart the backend to pick a scenario.</p>
          )}
          {!preview.empty && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 16 }}>
              <button className="btn on" disabled={!!busy || target?.kind === "refuse"} onClick={() => void apply()}>
                {busy ? "Applying…" : applyLabel}
              </button>
              <button className="btn" disabled={!!busy}
                onClick={() => { setPreview(null); setFile(null); if (input.current) input.current.value = ""; }}>
                Cancel
              </button>
              {busy && <span className="muted">Writing the changes into a draft — this can take a while.</span>}
            </div>
          )}
        </>
      )}

      {epicorPreview && (
        <div style={{ marginTop: plan ? 24 : 0 }}>
          <p className="muted" style={{ marginTop: 0 }}>
            Epicor data as of {formatCT(epicorPreview.pulledAt)}
          </p>
          {epicorPreview.brake.tripped && <BrakeWarning brake={epicorPreview.brake} />}
          <PlanView preview={epicorPreview} headerFields="description, status, order type"
            returnedLastSeen={epicorPreview.returnedLastSeen} archivedIds={epicorPreview.archivedIds} />
          <EpicorValueNote />
          {!epicorPreview.empty && target && (
            <TargetField preview={epicorPreview} value={targetName} onChange={setTargetName} target={target} />
          )}
          {!epicorPreview.empty && !serverKnowsTargets && (
            <p className="muted">This server is older than the screen, so the scenario can't be chosen here. Changes go into today's Day draft. Restart the backend to pick a scenario.</p>
          )}
          {!epicorPreview.empty && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 16 }}>
              <button className="btn on" disabled={!!busy || target?.kind === "refuse"} onClick={() => void applyEpicor()}>
                {busy ? "Applying…" : applyLabel}
              </button>
              <button className="btn" disabled={!!busy} onClick={() => setEpicorPreview(null)}>
                Cancel
              </button>
              {busy && <span className="muted">Writing the changes into a draft — this can take a while.</span>}
            </div>
          )}
        </div>
      )}

      {!!batches.length && (
        <Section title="Previous imports" n={batches.length}>
          <table className="qt">
            <thead><tr><th>When</th><th>Source</th><th className="r">Rows</th><th className="r">Changes</th><th>By</th></tr></thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.batchId} className="clickable"
                  onClick={(e) => void openPast(b.batchId, e.currentTarget.querySelector("button"))}>
                  <td className="mono">
                    {/* The row takes the click; the button is what a keyboard reaches.
                        Not while an apply is running: the screen it would hide is mid-write. */}
                    <button className="linkish mono" disabled={!!opening || !!busy}
                      title="Open this import as it was previewed"
                      onClick={(e) => { e.stopPropagation(); void openPast(b.batchId, e.currentTarget); }}>
                      {opening === b.batchId ? "Opening…" : formatCT(b.uploadedAt)}
                    </button>
                  </td>
                  <td>
                    {b.source === "file" ? `File: ${b.filename ?? "—"}` : "Epicor"}
                    {b.status !== "applied" && (
                      <>
                        {" "}<span className="chip exp">{batchChip(b.status)}</span>
                        {b.error && <div className="muted" style={{ fontSize: 11 }}>{b.error}</div>}
                      </>
                    )}
                  </td>
                  <td className="r">{b.rowCount ?? "—"}</td>
                  <td className="r">{b.changes}</td>
                  <td className="muted">{b.uploadedBy ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}
      </div>
    </div>
  );
}

/** More than a quarter of the active projects going missing reads as a bad pull more often than a real close-out. */
function BrakeWarning({ brake, past = false }: { brake: VanishBrake; past?: boolean }) {
  return past ? (
    <div className="run-warn">
      {brake.vanishing} of {brake.active} active projects were marked missing, more than a
      quarter. The preview warned of it before this was applied.
    </div>
  ) : (
    <div className="run-warn">
      {brake.vanishing} of {brake.active} active projects
      would be marked missing, more than a quarter. Check that the pull is complete before applying.
    </div>
  );
}

function EpicorValueNote() {
  return (
    <p className="muted" style={{ fontSize: 12 }}>
      Project value is not in the Epicor pull; existing values are kept, and new projects
      show — until a file upload.
    </p>
  );
}

/**
 * The two lines above a reopened import: what happened, when and by whom, and
 * where the rows came from. `when` formats a timestamp: the shop's Central
 * time, labeled CT, unless a caller says otherwise.
 */
export function pastImportHeader(
  b: PastImportBatch,
  when: (iso: string) => string = formatCT,
): { line: string; from: string } {
  const verb = b.status === "failed" ? "Failed"
    : b.status === "running" ? "Started"
    : "Applied";
  const who = b.uploadedByEmail ?? b.uploadedBy ?? "someone unrecorded";
  // A deleted scenario has no name left to give; saying which one it was is
  // better done by not pretending it is still there.
  // Undoing a batch deletes its draft and clears the link, so an undone batch
  // with no scenario did have one. A dangling id is kept as its own case:
  // scenario_id has no foreign key, so a draft deleted any other way leaves one.
  const into = b.scenarioName ? ` into scenario '${b.scenarioName}'`
    : b.scenarioId ? " into a scenario that has since been deleted"
    : b.status === "undone" ? " into a draft that was deleted when it was undone"
    : "";
  const from = b.source === "epicor"
    ? (b.pulledAt ? `From Epicor, data as of ${when(b.pulledAt)}` : "From Epicor")
    : `From file ${b.filename ?? "(no name recorded)"}`;
  return { line: `${verb} ${when(b.uploadedAt)} by ${who}${into}`, from };
}

/** Would this plan have written nothing? Unknown counts as nothing known, not as something. */
export function pastPlanEmpty(plan: PastImportPlan): boolean {
  return !plan.newProjects.length && !plan.dueDateMoves.length && !plan.headerChanges.length
    && !plan.vanished.length && (isUnknown(plan.returned) || !plan.returned.length);
}

/**
 * A past import, read-only: the plan it previewed, drawn by the same PlanView,
 * with no scenario field and no Apply. What it can still offer is the
 * scenario the changes went into, if that scenario is still there.
 */
function PastImportView({ past, onBack, onScenario }: {
  past: PastImport;
  onBack: () => void;
  onScenario?: (scenarioId: string) => void;
}) {
  const { batch, plan } = past;
  const head = pastImportHeader(batch);
  // Focus lands on the way back out, so a keyboard user is not left on a row
  // button that has just been hidden.
  const back = useRef<HTMLButtonElement>(null);
  useEffect(() => { back.current?.focus(); }, []);
  // What the log says each returned project was last seen as.
  const returnedLastSeen = Object.fromEntries(past.changes
    .filter((c) => c.kind === "returned").map((c) => [c.projectId, c.before]));
  return (
    <div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
        <button ref={back} className="btn" onClick={onBack}>Back to imports</button>
        {onScenario && batch.scenarioId && batch.scenarioName && (
          <button className="btn on" onClick={() => onScenario(batch.scenarioId!)}>Open scenario</button>
        )}
      </div>
      <p style={{ marginTop: 0, marginBottom: 2 }}>{head.line}</p>
      <p className="muted" style={{ marginTop: 0 }}>{head.from}</p>
      {batch.status === "undone" && (
        <div className="run-warn">This import was undone — its changes are no longer in effect.</div>
      )}
      {batch.status === "failed" && (
        <div className="err">
          This import failed partway, so only some of what is below was written.
          {batch.error && <div className="mono" style={{ fontSize: 12, marginTop: 4 }}>{batch.error}</div>}
        </div>
      )}
      {batch.status === "running" && (
        <div className="err">This import never finished, so only some of what is below was written.</div>
      )}
      {!batch.planRecorded && (
        <p className="muted" style={{ fontSize: 12 }}>
          This import predates saved previews; some details weren't recorded. They show as —.
        </p>
      )}
      {batch.brake?.tripped && <BrakeWarning brake={batch.brake} past />}
      <PlanView readOnly
        preview={{ rowCount: batch.rowCount, plan, empty: pastPlanEmpty(plan) }}
        headerFields={batch.source === "epicor" ? "description, status, order type" : undefined}
        returnedLastSeen={returnedLastSeen} />
      {batch.source === "epicor" && <EpicorValueNote />}
    </div>
  );
}

/**
 * The line under "Put changes into scenario". `blocks` is a verdict the apply
 * would refuse, so the screen disables Apply rather than letting it fail.
 */
export function targetHint(t: ImportTarget): { text: string; blocks: boolean } {
  if (t.kind === "refuse") return { text: t.reason, blocks: true };
  // Only the day's own name gets here while sharing it; without the note, the
  // hint would read as putting changes into the scenario the person can see
  // is live.
  const shared = t.sharedWith === "published" ? " (the live schedule has the same name)"
    : t.sharedWith === "archived" ? " (an archived scenario has the same name)" : "";
  if (t.kind === "existing") {
    return shared
      ? { text: `Adds to the draft '${t.name}'${shared}.`, blocks: false }
      : { text: `Adds to the existing draft '${t.name}'.`, blocks: false };
  }
  return { text: `Creates a new draft '${t.name}'${shared}.`, blocks: false };
}

/**
 * Where the changes go. Prefilled with today's draft, which is where they went
 * before there was a choice — so a person who ignores the field gets exactly
 * that. The suggestions are drafts only; the live schedule is never offered.
 */
function TargetField({ preview, value, onChange, target }: {
  preview: ImportPreview;
  value: string;
  onChange: (v: string) => void;
  target: ImportTarget;
}) {
  const hint = targetHint(target);
  return (
    <div style={{ marginTop: 16, maxWidth: 420 }}>
      <label className="fld">
        <span>Put changes into scenario</span>
        <input list="import-target-drafts" value={value} onChange={(e) => onChange(e.target.value)} />
      </label>
      <datalist id="import-target-drafts">
        {/* Two drafts can share a name; one suggestion covers both, and the hint refuses it. */}
        {[...new Set(preview.draftScenarioNames)].map((n) => <option key={n} value={n} />)}
      </datalist>
      {hint.blocks
        ? <div className="err" style={{ marginTop: 6, marginBottom: 0 }}>{hint.text}</div>
        : <p className="hint" style={{ marginTop: 4 }}>{hint.text}</p>}
    </div>
  );
}

/**
 * A batch still `running` when the list is read is, as far as this screen can
 * know, one whose process died mid-apply — so it reads as incomplete, styled
 * like a failure. Every running row gets this, including one some other
 * session is applying right now: the apply response carries no batch id to
 * tell them apart, and this component only reloads the list after its own
 * apply finishes, by which point that row has left `running`.
 */
function batchChip(status: ImportBatch["status"]): string {
  return status === "running" ? "incomplete" : status;
}

/**
 * The plan itself — same rendering whether it came from the file or a live
 * Epicor pull, so a reader learns one table shape rather than two.
 */
function PlanView({ preview, headerFields = "description, status, value", readOnly = false, returnedLastSeen = {}, archivedIds = [] }: {
  /** A preview, or a past import's plan — whose row count or details may not have been recorded. */
  preview: { rowCount: number | null; plan: PastImportPlan; empty: boolean };
  /** What the header-changes KPI says it covers — the live pull owns no value. */
  headerFields?: string;
  /** A past import: the plan as it was, with nothing left to apply. */
  readOnly?: boolean;
  /** When each returned project was last seen, where that is known. */
  returnedLastSeen?: Record<string, string | null>;
  /** Archived projects among the vanished and returned: finished work, kept out of the warnings. */
  archivedIds?: string[];
}) {
  const plan = preview.plan;
  const archived = new Set(archivedIds);
  // A past import happened; its counts say what was done, not what would be.
  const sub = readOnly
    ? { created: "created", moved: "moved, with their steps", flagged: "flagged, not deleted", unchanged: "left as they were" }
    : { created: "will be created", moved: "steps re-date themselves", flagged: "flagged, never deleted", unchanged: "nothing to do" };
  const allReturned = isUnknown(plan.returned) ? [] : plan.returned;
  // A finished, archived project leaving the file is what completion looks like,
  // so it is not a warning; one coming back is not "back in the file" either —
  // it stays archived and is flagged for a manager instead.
  const vanished = plan.vanished.filter((v) => !archived.has(v.projectId));
  const archivedGone = plan.vanished.length - vanished.length;
  const returned = allReturned.filter((id) => !archived.has(id));
  const archivedBack = allReturned.filter((id) => archived.has(id));
  return (
    <>
      <div className="kpis">
        <Kpi label="Rows read" value={preview.rowCount} sub="job rows, collapsed to projects" />
        <Kpi label="New projects" value={plan.newProjects.length} sub={sub.created} crit={false} />
        <Kpi label="Due dates moved" value={plan.dueDateMoves.length} sub={sub.moved} />
        <Kpi label="Header changes" value={plan.headerChanges.length} sub={headerFields} />
        <Kpi label="Gone from the file" value={vanished.length} sub={sub.flagged} crit={vanished.length > 0} />
        <Kpi label="Unchanged" value={plan.unchanged} sub={sub.unchanged} />
      </div>

      {preview.empty ? (
        readOnly ? (
          <p className="muted">This import found nothing new, so it changed nothing.</p>
        ) : (
          <p className="muted">
            This file says nothing new. Applying it would change nothing, so there is nothing
            to review.
          </p>
        )
      ) : (
        <>
          {!!plan.newProjects.length && (
            <Section title="New projects" n={plan.newProjects.length}>
              <table className="qt">
                <thead><tr><th>Project</th><th>Description</th><th>Product</th><th>Due</th><th>Breakdown</th></tr></thead>
                <tbody>
                  {plan.newProjects.map((p) => (
                    <tr key={p.projectId}>
                      <td className="mono"><b>{p.projectId}</b></td>
                      <td>{p.description ?? "—"}</td>
                      <td className="mono">{isUnknown(p.productCode) ? "—" : p.productCode ?? "—"}</td>
                      <td className="mono">
                        {isUnknown(p.dueDate) ? "—" : p.dueDate ?? <span className="muted">no date</span>}
                      </td>
                      <td>
                        {isUnknown(p.template) ? "—"
                          : p.template
                            ? <>from <b>{p.template.name}</b></>
                            : <span className="chip exp">needs a breakdown</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}

          {!!plan.dueDateMoves.length && (
            <Section title="Due dates that moved" n={plan.dueDateMoves.length}>
              <table className="qt">
                <thead><tr><th>Project</th><th>From</th><th>To</th><th className="r">Steps that follow it</th></tr></thead>
                <tbody>
                  {plan.dueDateMoves.map((m) => (
                    <tr key={m.projectId}>
                      <td className="mono"><b>{m.projectId}</b></td>
                      <td className="mono">{m.from ?? "—"}</td>
                      <td className={`mono ${m.from && m.to && m.to < m.from ? "late" : ""}`}>{m.to ?? "—"}</td>
                      <td className="r">
                        {isUnknown(m.phasesAffected) ? "—"
                          : m.phasesAffected === 0
                            ? <span className="muted">none — every step is pinned</span>
                            : m.phasesAffected}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}

          {!!vanished.length && (
            <Section title="No longer in the file" n={vanished.length}>
              <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
                Flagged, not deleted — their steps and assignments stay exactly as they are.
                {!!archivedGone && ` ${archivedGone} archived project${archivedGone === 1 ? " is" : "s are"} also gone, as finished work is — not counted here.`}
              </p>
              <table className="qt">
                <thead><tr><th>Project</th><th>Last seen</th><th className="r">Open steps</th></tr></thead>
                <tbody>
                  {vanished.map((v) => (
                    <tr key={v.projectId}>
                      <td className="mono"><b>{v.projectId}</b></td>
                      <td className="mono">{v.lastSeen ?? "—"}</td>
                      <td className="r">{isUnknown(v.openPhases) ? "—" : v.openPhases}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}

          {!!returned.length && (
            <Section title="Back in the file" n={returned.length}>
              <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
                {readOnly ? "Were" : "Will be"} marked active again — they had been flagged as gone from the file.
              </p>
              <table className="qt">
                <thead><tr><th>Project</th><th>Last seen</th></tr></thead>
                <tbody>
                  {returned.map((id) => (
                    <tr key={id}>
                      <td className="mono"><b>{id}</b></td>
                      <td className="mono">{returnedLastSeen[id] ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}

          {!!archivedBack.length && (
            <Section title="Archived, but back in Epicor" n={archivedBack.length}>
              <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
                {readOnly ? "These stayed" : "These stay"} archived. {readOnly ? "They were" : "They will be"} flagged on the
                Archived list so a manager can restore any that are real work again.
              </p>
              <table className="qt">
                <thead><tr><th>Project</th><th>Last seen</th></tr></thead>
                <tbody>
                  {archivedBack.map((id) => (
                    <tr key={id}>
                      <td className="mono"><b>{id}</b></td>
                      <td className="mono">{returnedLastSeen[id] ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}

          {!!plan.headerChanges.length && (
            <Section title="Header changes" n={plan.headerChanges.length}>
              <table className="qt">
                <thead><tr><th>Project</th><th>Field</th><th>From</th><th>To</th></tr></thead>
                <tbody>
                  {plan.headerChanges.map((h, i) => (
                    <tr key={i}>
                      <td className="mono">{h.projectId}</td>
                      <td className="mono">{h.field}</td>
                      <td className="muted">{String(h.from ?? "—")}</td>
                      <td>{String(h.to ?? "—")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}
        </>
      )}
    </>
  );
}

function Section({ title, n, children }: { title: string; n: number; children: React.ReactNode }) {
  return (
    <div className="run-sec">
      <h3>{title}<span className="run-sec-n">{n}</span></h3>
      <div className="scroll">{children}</div>
    </div>
  );
}

function Kpi({ label, value, sub, crit }: {
  label: string; value: Unknowable<number> | null; sub: string; crit?: boolean;
}) {
  return (
    <div className="kpi">
      <span className="lab">{label}</span>
      <span className={`val${crit ? " crit" : ""}`}>{value === null || isUnknown(value) ? "—" : value}</span>
      <span className="sub">{sub}</span>
    </div>
  );
}
